import React, { useEffect, useState } from "react"
import {
    Box,
    Typography,
    Paper,
    CircularProgress,
    Alert,
    Chip,
} from "@mui/material"
import Grid from "@mui/material/Grid"
import StorefrontIcon from "@mui/icons-material/Storefront"
import PointOfSaleIcon from "@mui/icons-material/PointOfSale"
import PeopleIcon from "@mui/icons-material/People"
import TrendingUpIcon from "@mui/icons-material/TrendingUp"
import {
    getMyAssignments,
    OutletAssignment,
} from "../../services/assignmentService"
import apiClient from "../../services/apiClient"
import { useTranslation } from "react-i18next"

interface OutletStats {
    outlet: OutletAssignment
    registerCount: number
    todaySalesCount: number
    todayRevenue: number
}

function ManagerDashboard() {
    const { t } = useTranslation()
    const [outletStats, setOutletStats] = useState<OutletStats[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        let cancelled = false

        async function load() {
            try {
                const assignments = await getMyAssignments()
                const outlets = assignments.outletAssignments

                const stats: OutletStats[] = []

                for (const outlet of outlets) {
                    let registerCount = 0
                    try {
                        const regRes = await apiClient.get(
                            `/api/registers?outlet_id=${outlet.outlet_id}`
                        )
                        registerCount = (regRes.registers || []).length
                    } catch {
                        // ignore
                    }

                    stats.push({
                        outlet,
                        registerCount,
                        todaySalesCount: 0,
                        todayRevenue: 0,
                    })
                }

                if (!cancelled) {
                    setOutletStats(stats)
                }
            } catch (e: any) {
                if (!cancelled)
                    setError(e.message || "Failed to load dashboard")
            } finally {
                if (!cancelled) setLoading(false)
            }
        }

        load()
        return () => {
            cancelled = true
        }
    }, [])

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress />
            </Box>
        )
    }

    if (error) {
        return <Alert severity="error">{error}</Alert>
    }

    return (
        <Box>
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" fontWeight={700} gutterBottom>
                    {t("dashboard.manager.title")}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    {t("dashboard.manager.subtitle")}
                </Typography>
            </Box>

            {/* Summary cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 3,
                            borderRadius: 3,
                            border: "1px solid",
                            borderColor: "divider",
                            textAlign: "center",
                        }}
                    >
                        <StorefrontIcon
                            sx={{ fontSize: 40, color: "primary.main", mb: 1 }}
                        />
                        <Typography variant="h3" fontWeight={700}>
                            {outletStats.length}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {t("dashboard.manager.outlets")}
                        </Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 3,
                            borderRadius: 3,
                            border: "1px solid",
                            borderColor: "divider",
                            textAlign: "center",
                        }}
                    >
                        <PointOfSaleIcon
                            sx={{
                                fontSize: 40,
                                color: "secondary.main",
                                mb: 1,
                            }}
                        />
                        <Typography variant="h3" fontWeight={700}>
                            {outletStats.reduce(
                                (acc, s) => acc + s.registerCount,
                                0
                            )}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {t("dashboard.manager.registers")}
                        </Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 3,
                            borderRadius: 3,
                            border: "1px solid",
                            borderColor: "divider",
                            textAlign: "center",
                        }}
                    >
                        <TrendingUpIcon
                            sx={{ fontSize: 40, color: "success.main", mb: 1 }}
                        />
                        <Typography variant="h3" fontWeight={700}>
                            {outletStats.reduce(
                                (acc, s) => acc + s.todaySalesCount,
                                0
                            )}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {t("dashboard.manager.salesToday")}
                        </Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 3,
                            borderRadius: 3,
                            border: "1px solid",
                            borderColor: "divider",
                            textAlign: "center",
                        }}
                    >
                        <PeopleIcon
                            sx={{ fontSize: 40, color: "warning.main", mb: 1 }}
                        />
                        <Typography variant="h3" fontWeight={700}>
                            &mdash;
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {t("dashboard.manager.activeCashiers")}
                        </Typography>
                    </Paper>
                </Grid>
            </Grid>

            {/* Outlet details */}
            <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>
                {t("dashboard.manager.myOutlets")}
            </Typography>
            <Grid container spacing={3}>
                {outletStats.map((stat) => (
                    <Grid item xs={12} md={6} key={stat.outlet.outlet_id}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 3,
                                borderRadius: 3,
                                border: "1px solid",
                                borderColor: "divider",
                                "&:hover": {
                                    borderColor: "primary.main",
                                    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                                },
                                transition: "all 0.2s",
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    mb: 2,
                                }}
                            >
                                <StorefrontIcon
                                    sx={{
                                        color: "primary.main",
                                        mr: 1.5,
                                        fontSize: 28,
                                    }}
                                />
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="h6" fontWeight={600}>
                                        {stat.outlet.outlet_name}
                                    </Typography>
                                </Box>
                                <Chip
                                    label={stat.outlet.role}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                />
                            </Box>
                            <Grid container spacing={2}>
                                <Grid item xs={4}>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        {t("dashboard.manager.registers")}
                                    </Typography>
                                    <Typography variant="h6" fontWeight={700}>
                                        {stat.registerCount}
                                    </Typography>
                                </Grid>
                                <Grid item xs={4}>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        {t("dashboard.manager.salesDay")}
                                    </Typography>
                                    <Typography variant="h6" fontWeight={700}>
                                        {stat.todaySalesCount}
                                    </Typography>
                                </Grid>
                                <Grid item xs={4}>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        {t("dashboard.manager.revenueDay")}
                                    </Typography>
                                    <Typography variant="h6" fontWeight={700}>
                                        {stat.todayRevenue > 0
                                            ? `${(
                                                  stat.todayRevenue / 100
                                              ).toFixed(2)} €`
                                            : "—"}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>
                ))}
            </Grid>

            {outletStats.length === 0 && (
                <Alert severity="info" sx={{ mt: 2 }}>
                    {t("dashboard.manager.noOutlet")}
                </Alert>
            )}
        </Box>
    )
}

export default ManagerDashboard
